import { BEHAVIOUR_TYPES, defaultBusinessConfig } from './behaviourCatalog';

const isField = (n) => n && n.type === 'FIELD' && n.fieldCode;
const isConst = (n) => n && n.type === 'CONSTANT';
const isPrev = (n) => n && n.type === 'PREVIOUS_RA_FIELD';
const opOf = (n) => (n && String(n.operator || '').toUpperCase()) || '';
const argsOf = (n) => (Array.isArray(n?.arguments) ? n.arguments : []);

/**
 * Reverse-map known AST patterns into businessConfig.
 * Unknown/complex trees → CUSTOM_FORMULA (preserves formulaJson unchanged).
 */
export const astToBusinessConfig = (formulaJson, field = {}) => {
  const base = defaultBusinessConfig({
    lineReference: field.metadata?.businessConfig?.lineReference || '',
    financialClassification: field.metadata?.businessConfig?.financialClassification || 'REFERENCE',
    financialEffect: field.metadata?.businessConfig?.financialEffect || 'INFORMATION',
    includeInGroup: field.metadata?.businessConfig?.includeInGroup || 'NONE',
    previousBehaviour: field.metadata?.businessConfig?.previousBehaviour || 'SAME_FIELD',
    comparisonBehaviour: field.metadata?.businessConfig?.comparisonBehaviour || 'CURRENT_MINUS_PREVIOUS',
  });

  if (!formulaJson) {
    if (String(field.fieldType || '').toUpperCase() === 'PREVIOUS_RA') {
      return {
        ...base,
        behaviourType: BEHAVIOUR_TYPES.PREVIOUS_RA_VALUE,
        previousBehaviour: 'SAME_FIELD',
        previousFieldCode: field.fieldCode,
      };
    }
    return {
      ...base,
      behaviourType: BEHAVIOUR_TYPES.MANUAL_ENTRY,
    };
  }

  // PREVIOUS_RA_FIELD alone
  if (isPrev(formulaJson) && !formulaJson.operator) {
    return {
      ...base,
      behaviourType: BEHAVIOUR_TYPES.PREVIOUS_RA_VALUE,
      previousBehaviour: formulaJson.fieldCode === field.fieldCode ? 'SAME_FIELD' : 'OTHER_FIELD',
      previousFieldCode: formulaJson.fieldCode,
    };
  }

  const op = opOf(formulaJson);
  const args = argsOf(formulaJson);

  // SUM / ADD of fields
  if ((op === 'SUM' || op === 'ADD') && args.length >= 1 && args.every(isField)) {
    const codes = args.map((a) => a.fieldCode);
    const isAuto = field.metadata?.businessConfig?.behaviourType === BEHAVIOUR_TYPES.AUTO_TOTAL
      || ['TOTAL_RECOVERIES', 'TOTAL_WITHHELD', 'TOTAL_DEDUCTIONS'].includes(field.semanticRole);
    return {
      ...base,
      behaviourType: isAuto ? BEHAVIOUR_TYPES.AUTO_TOTAL : BEHAVIOUR_TYPES.SUM_OF_FIELDS,
      sumFieldCodes: codes,
      includeInGroup: isAuto
        ? (field.semanticRole || base.includeInGroup)
        : base.includeInGroup,
    };
  }

  // SUBTRACT of fields
  if (op === 'SUBTRACT' && args.length >= 2 && args.every(isField)) {
    return {
      ...base,
      behaviourType: BEHAVIOUR_TYPES.SUBTRACT_FIELDS,
      subtractFieldCodes: args.map((a) => a.fieldCode),
      minuendFieldCode: args[0].fieldCode,
      sumFieldCodes: args.slice(1).map((a) => a.fieldCode),
    };
  }

  // CURRENT - PREVIOUS
  if (op === 'SUBTRACT' && args.length === 2 && isField(args[0]) && isPrev(args[1])) {
    return {
      ...base,
      behaviourType: BEHAVIOUR_TYPES.CURRENT_MINUS_PREVIOUS,
      baseFieldCode: args[0].fieldCode,
      previousFieldCode: args[1].fieldCode,
      previousBehaviour: 'OTHER_FIELD',
    };
  }

  // PERCENTAGE(base, rate)
  if (op === 'PERCENTAGE' && args.length === 2 && isField(args[0])) {
    const rateArg = args[1];
    const cfg = {
      ...base,
      behaviourType: BEHAVIOUR_TYPES.PERCENTAGE,
      baseFieldCode: args[0].fieldCode,
      rateSource: 'FIXED',
      rateValue: isConst(rateArg) ? Number(rateArg.value) : null,
    };
    if (rateArg?.type === 'DEPARTMENT_FIELD') {
      cfg.rateSource = 'DEPARTMENT';
      cfg.rateFieldCode = rateArg.fieldCode;
      cfg.rateValue = rateArg.fallback;
    } else if (rateArg?.type === 'PROJECT_FIELD') {
      cfg.rateSource = 'PROJECT';
      cfg.rateFieldCode = rateArg.fieldCode;
      cfg.rateValue = rateArg.fallback;
    } else if (rateArg?.type === 'AGREEMENT_FIELD') {
      cfg.rateSource = 'AGREEMENT';
      cfg.rateFieldCode = rateArg.fieldCode;
      cfg.rateValue = rateArg.fallback;
    }
    return cfg;
  }

  // MIN(PERCENTAGE(...), cap) → capped percentage or balance recovery
  if (op === 'MIN' && args.length === 2) {
    const pct = args.find((a) => opOf(a) === 'PERCENTAGE');
    const other = args.find((a) => a !== pct);
    if (pct && isField(argsOf(pct)[0])) {
      const inner = astToBusinessConfig(pct, field);
      if (isPrev(other)) {
        return {
          ...inner,
          behaviourType: BEHAVIOUR_TYPES.BALANCE_RECOVERY,
          previousOutstandingFieldCode: other.fieldCode,
          capToOutstanding: true,
          recoveryMethod: 'PERCENTAGE',
        };
      }
      return {
        ...inner,
        behaviourType: BEHAVIOUR_TYPES.CAPPED_PERCENTAGE,
        capValue: isConst(other) ? Number(other.value) : null,
        capBaseFieldCode: isField(other) ? other.fieldCode : (opOf(other) === 'PERCENTAGE' && isField(argsOf(other)[0]) ? argsOf(other)[0].fieldCode : null),
        capType: other?.type === 'AGREEMENT_FIELD' ? 'AGREEMENT' : 'FIXED',
      };
    }
  }

  // MULTIPLY(qty, rate)
  if (op === 'MULTIPLY' && args.length === 2) {
    return {
      ...base,
      behaviourType: BEHAVIOUR_TYPES.QUANTITY_RATE,
      quantityFieldCode: isField(args[0]) ? args[0].fieldCode : null,
      quantityValue: isConst(args[0]) ? Number(args[0].value) : null,
      rateFieldCode: isField(args[1]) ? args[1].fieldCode : null,
      rateValue: isConst(args[1]) ? Number(args[1].value) : null,
    };
  }

  // IF
  if (op === 'IF' || formulaJson.condition) {
    return {
      ...base,
      behaviourType: BEHAVIOUR_TYPES.CONDITIONAL,
      conditionTree: field.metadata?.businessConfig?.conditionTree || {
        logic: 'AND',
        conditions: [],
      },
      elseValue: isConst(formulaJson.else) ? Number(formulaJson.else.value) : 0,
    };
  }

  // Fallback — preserve AST via CUSTOM_FORMULA
  return {
    ...base,
    behaviourType: BEHAVIOUR_TYPES.CUSTOM_FORMULA,
  };
};

/** Ensure field has businessConfig (hydrate from AST if missing). */
export const ensureBusinessConfig = (field) => {
  if (field?.metadata?.businessConfig) return field;
  const cfg = astToBusinessConfig(field?.formulaJson, field);
  return {
    ...field,
    metadata: {
      ...(field.metadata && typeof field.metadata === 'object' ? field.metadata : {}),
      businessConfig: cfg,
    },
  };
};

export const hydrateTemplateFields = (fields) =>
  (fields || []).map(ensureBusinessConfig);
